import React, { useState, createContext, useMemo, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store';
import styled from 'styled-components';
import { selectAvailableBadges, selectSelectedBadges } from '@/store/slices/playerBadges';
import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';
import { updateHallOfFame } from '@/store/slices/playerStats';
import triggerStateLoaderIfNeeded from '@/page-components/utils/hooks/triggerStateLoaderIfNeeded';

export const HallOfFameContext = createContext(null);

type ModuleStateProps = {
  isModalOpen: boolean;
};

type TemplateProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: any;
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

// Provider component
export const HallOfFame = (componentProps: TemplateProps) => {
  let props = componentProps;

  const dataElementContext = React.useContext(DataElementContext);
  [props] = processComponentProps(props, dataElementContext);

  const dispatch = useAppDispatch();

  triggerStateLoaderIfNeeded('state.playerBadges');

  const allBadges = useAppSelector(selectAvailableBadges);

  // Split badges into available and locked based on unlocked_at
  const availableBadges = useMemo(() => {
    return allBadges?.filter(badge => badge.unlocked_at) || [];
  }, [allBadges]);

  const lockedBadges = useMemo(() => {
    return allBadges?.filter(badge => !badge.unlocked_at) || [];
  }, [allBadges]);

  const reduxSelectedBadges = useAppSelector(selectSelectedBadges);
  
  const defaultBadges = useMemo(() => [{}, {}, {}], []);
  
  const initialSelectedBadges = useMemo(() => {
    return reduxSelectedBadges && reduxSelectedBadges.length > 0 ? reduxSelectedBadges : defaultBadges;
  }, [reduxSelectedBadges, defaultBadges]);

  const initialState = {
    isModalOpen: false,
  };

  const [state, setState] = React.useState<ModuleStateProps>(initialState);
  
  const [selectedBadges, setSelectedBadges] = useState(() => initialSelectedBadges);
  const [savedSelectedBadges, setSavedSelectedBadges] = useState(() => initialSelectedBadges);
  const [currentSlotIndex, setCurrentSlotIndex] = useState(0);

  React.useEffect(() => {
    if (reduxSelectedBadges && reduxSelectedBadges.length > 0) {
      const isDataDifferent = JSON.stringify(reduxSelectedBadges) !== JSON.stringify(savedSelectedBadges);
      
      if (isDataDifferent) {
        setSavedSelectedBadges(reduxSelectedBadges);
        setSelectedBadges(reduxSelectedBadges);
      }
    }
  }, [reduxSelectedBadges]);

  // console.log('HallOfFame[initialSelectedBadges]', initialSelectedBadges);
  // console.log('HallOfFame[selectedBadges]', selectedBadges);
  // console.log('HallOfFame[savedSelectedBadges]', savedSelectedBadges);
  
  // Function to check if a badge ID is in the selectedBadges array
  const isBadgeInSelection = useCallback((badgeId: number | string) => {
    if (!badgeId) return false;
    
    // Convert to string for comparison since badge IDs are hashes
    const id = String(badgeId);
    
    // Check if this ID exists in any of the selected badges
    return selectedBadges.some(badge => String(badge.id) === id);
  }, [selectedBadges]);
  
  // Find the slot index where a badge is selected
  const findBadgeSlotIndex = useCallback((badgeId: number | string) => {
    if (!badgeId) return -1;
    
    // Convert to string for comparison since badge IDs are hashes
    const id = String(badgeId);
    
    // Return the index of the slot containing this badge ID, or -1 if not found
    return selectedBadges.findIndex(badge => {
      if(!badge) return false;
      return String(badge.id) === id;
    });
  }, [selectedBadges]);
  
  // Select or deselect badge based on click
  const selectBadge = useCallback((event: any) => {
    event.preventDefault();
    event.stopPropagation();
    
    const badgeId = event.target.dataset.id; // Keep as string since it's a hash
    // console.log('HallOfFame[selectBadge]', badgeId, currentSlotIndex, selectedBadges);
    
    if (badgeId) {
      // Check if this badge is already selected in ANY slot
      const existingBadgeSlot = findBadgeSlotIndex(badgeId);
      const isAlreadySelected = existingBadgeSlot !== -1;
      const badgeObj = availableBadges?.find((badge: { id: string }) => String(badge.id) === String(badgeId));

      setSelectedBadges(prev => {
        const newBadges = [...prev];
        
        if (isAlreadySelected) {
          // Remove the badge by setting it to an empty object
          newBadges[existingBadgeSlot] = {};
        } else {
          // Find the first empty slot to add the badge
          const firstEmptySlot = newBadges.findIndex(badge => !badge || !badge.id);
          if (firstEmptySlot !== -1) {
            newBadges[firstEmptySlot] = badgeObj || {};
            // Update current slot index to the next empty slot after this one
            setTimeout(() => {
              const nextEmptySlot = newBadges.findIndex((badge, index) => 
                index > firstEmptySlot && (!badge || !badge.id)
              );
              if (nextEmptySlot !== -1) {
                setCurrentSlotIndex(nextEmptySlot);
              }
            }, 0);
          }
        }
        
        return newBadges;
      });
    }
  }, [findBadgeSlotIndex, availableBadges]);

  const onToggle = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
      setState((v) => ({
        ...v,
        isModalOpen: !v.isModalOpen,
      }));
    };

    const onClose = (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
        setState((v) => ({
          ...v,
          isModalOpen: false,
        }));
      };
  

  const saveSelection = useCallback(() => {
    // console.log('HallOfFame[saveSelection]', selectedBadges);
    dispatch(updateHallOfFame(selectedBadges));
    setSavedSelectedBadges(selectedBadges);
    setState((v) => ({
      ...v,
      isModalOpen: false,
    }));

  }, [dispatch, selectedBadges]);
  
  // Memoized context value
  const contextValue = useMemo(() => {
    
    return {
      // Badge data
      isModalOpen: state.isModalOpen,
      savedSelectedBadges,
      selectedBadges,
      allBadges,
      availableBadges,
      lockedBadges,
      
      // Actions
      selectBadge,
      saveSelection,
      
      // Helper methods
      isBadgeSelected: isBadgeInSelection,
      onToggle,
      onClose,
    };
  }, [state, allBadges, availableBadges, lockedBadges, savedSelectedBadges, selectedBadges, selectBadge, saveSelection, isBadgeInSelection]);


  // console.log('HallOfFame[contextValue]', contextValue);

  return (
    <ModuleElementDiv className={componentProps.className ?? ''} $styleText={componentProps.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default HallOfFame;

